/**
 * DocumentFooter - Shared footer for quotes and invoices
 * PROMPT 40: Financial Documents Unified View Layer
 * EF-QUOTE-STRUCTURE-V1: Added tenant branding at footer bottom
 * EF-QUOTE-UX-REFINEMENT-LOCK-V3: Single-column centered layout matching PDF
 */
import { Phone, Mail, Globe } from "lucide-react";

export function DocumentFooter({
  notes,
  paymentTerms,
  bankDetails,
  footerText,
  validUntil, // For quotes
  formatDate,
  // EF-QUOTE-STRUCTURE-V1: Tenant branding
  companyProfile,
}) {
  return (
    <>
      {/* Notes Section */}
      {notes && (
        <div className="p-6 border-t border-border bg-surface-elevated print:bg-white">
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Notes
          </h2>
          <p className="text-sm text-foreground whitespace-pre-line" data-testid="document-notes">
            {notes}
          </p>
        </div>
      )}

      {/* Footer Text / Validity */}
      {(footerText || validUntil) && (
        <div className="p-4 border-t border-border bg-surface-elevated print:bg-white">
          <div className="text-center text-xs text-muted-foreground">
            {footerText && (
              <p className="whitespace-pre-line">{footerText}</p>
            )}
            {validUntil && (
              <p className={footerText ? "mt-2" : ""}>
                This quote is valid until {formatDate(validUntil)}.
              </p>
            )}
          </div>
        </div>
      )}

      {/* EF-QUOTE-UX-REFINEMENT-LOCK-V3: Single-column Company Footer (matches PDF exactly) */}
      {companyProfile && (
        <div className="border-t-2 border-border bg-slate-50 p-6 print:bg-white">
          <div className="flex flex-col items-center text-center text-xs text-muted-foreground space-y-4">
            {/* Tenant Logo — EVENTSFLO-PLATFORM-LOGO-DOCUMENT-LOGO-SEPARATION-V1:
                Prefer the Quote/Invoice document logo; fall back to the
                Platform logo when no dedicated document logo is set. */}
            {(companyProfile.document_logo_url || companyProfile.logo_url) && (
              <img 
                src={companyProfile.document_logo_url || companyProfile.logo_url} 
                alt={companyProfile.company_name || "Company Logo"}
                className="h-10 max-w-[120px] object-contain"
                data-testid="document-tenant-logo-footer"
              />
            )}
            
            {/* Company Name & Address */}
            <div className="space-y-0.5">
              {companyProfile.company_name && (
                <p className="font-semibold text-foreground text-sm">{companyProfile.company_name}</p>
              )}
              {companyProfile.address_line1 && (
                <p>{companyProfile.address_line1}</p>
              )}
              {companyProfile.address && !companyProfile.address_line1 && (
                <p>{companyProfile.address}</p>
              )}
              {(companyProfile.city || companyProfile.postcode) && (
                <p>{companyProfile.city} {companyProfile.postcode}</p>
              )}
            </div>
            
            {/* Contact Details */}
            <div className="space-y-0.5">
              {companyProfile.contact_phone && (
                <p className="flex items-center justify-center gap-1">
                  <Phone className="h-3 w-3" />
                  {companyProfile.contact_phone}
                </p>
              )}
              {companyProfile.contact_email && (
                <p className="flex items-center justify-center gap-1">
                  <Mail className="h-3 w-3" />
                  {companyProfile.contact_email}
                </p>
              )}
              {companyProfile.vat_number && (
                <p>VAT: {companyProfile.vat_number}</p>
              )}
            </div>
            
            {/* Bank Details */}
            {(companyProfile.bank_details_text || companyProfile.iban || companyProfile.bic) && (
              <div className="space-y-0.5 font-mono text-[10px]">
                {companyProfile.bank_details_text && (
                  <p className="whitespace-pre-line">{companyProfile.bank_details_text}</p>
                )}
                {companyProfile.iban && (
                  <p>IBAN: {companyProfile.iban}</p>
                )}
                {companyProfile.bic && (
                  <p>BIC: {companyProfile.bic}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default DocumentFooter;
